<script lang="ts">
    import { onMount } from 'svelte'
    import dayjs from 'dayjs'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Popover from '../_common/popover.svelte'
    import AppointmentForm from '../agenda/appointment-form.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'
    import Zgrid from '../_common/z/zgrid.svelte'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import type { Service } from '../../modules/posApi/services/service'
    import type { ProductOpenUnit } from '../../modules/posApi/stock/product-consumption/product-open-unit'
    import type { ProductConsumptionLog } from '../../modules/posApi/stock/product-consumption/product-consumption-log'
    import type { ProductMovement } from '../../modules/posApi/stock/product-movements/product-movement'
    import type { AppointmentConsumption } from '../../modules/schedulerApi/appointments/appointment-consumption'

    let show = false
    let serviceMap: Record<string, Service> = {}
    let viewAppointmentId: string = null

    let expirationGrid: GridClass = new GridClass()
    let minStockGrid: GridClass = new GridClass()
    let openUnitsGrid: GridClass = new GridClass()
    let varianceGrid: GridClass = new GridClass()
    let recentConsumptionGrid: GridClass = new GridClass()
    let pendingSyncGrid: GridClass = new GridClass()

    // correções manuais de frasco — mesma permissão do "Ajuste" de estoque (ver stock-home.svelte), já que
    // servem pra consertar lançamentos de abertura/finalização errados vindos da agenda
    const canCorrectUnits = !!(window.store.token.permissions.stock?.inputs?.write && window.store.token.permissions.stock?.outputs?.write)

    onMount(async () => {
        await loadDashboard()
    })

    async function loadDashboard() {
        try {
            show = true
            const from = dayjs().add(-7, 'day').toDate()
            const [allServices, openUnits, closedUnits, logs, discreteConsumptions, failedSync] = await Promise.all([
                // excluded:true traz também produtos removidos — necessário pra resolver o nome em
                // frascos/logs/pendências antigos que referenciam um insumo já excluído desde então.
                window.api.pos.services.getAll({ type: ['product', 'consumable'], excluded: true }),
                window.api.pos.stock.productConsumption.getAll({ status: 'open' }),
                window.api.pos.stock.productConsumption.getAll({ status: 'closed' }),
                // consumo fracionado (estimativa, não afeta o ledger) — ver ProductConsumptionLog
                window.api.pos.stock.productConsumption.findLogs({ from }),
                // consumo discreto (afeta o ledger direto, ex: luva) — vira ProductMovement, não log
                window.api.pos.stock.productMovements.getAll({ from, type: 'out_consumption' }),
                window.api.schedulers.appointmentConsumption.findFailedSync(),
            ])

            serviceMap = {}
            for (const service of allServices as Service[]) {
                serviceMap[service.serviceId] = service
            }
            // vencimento/estoque mínimo só fazem sentido pra produtos ativos (status >= PAUSED)
            const activeServices = (allServices as Service[]).filter((el) => el.status >= 70)

            // consumo já declarado por frasco aberto — busca em lote (uma query, não uma por frasco)
            const loggedSums = await window.api.pos.stock.productConsumption.sumLoggedForUnits(
                (openUnits as ProductOpenUnit[]).map((el) => el.productOpenUnitId),
            )

            loadExpirationGrid(activeServices)
            loadMinStockGrid(activeServices)
            loadOpenUnitsGrid(openUnits, loggedSums)
            loadVarianceGrid(closedUnits)
            loadRecentConsumptionGrid(logs, discreteConsumptions)
            loadPendingSyncGrid(failedSync)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function baseGrid(title: string, subtitle: string): GridClass {
        const grid = new GridClass()
        grid.title = title
        grid.subtitle = subtitle
        grid.showActs = false
        return grid
    }

    function loadExpirationGrid(services: Service[]) {
        const limit = dayjs().add(30, 'day').toDate()
        const items = services
            .filter((el) => el.balance && el.nextExpirationDate && new Date(el.nextExpirationDate) <= limit)
            .sort((a, b) => new Date(a.nextExpirationDate).getTime() - new Date(b.nextExpirationDate).getTime())

        const grid = baseGrid(
            'Produtos a vencer ou vencidos',
            'Produtos com vencimento cadastrado nos próximos 30 dias ou já vencidos, com base no lote mais próximo do estoque atual.',
        )
        grid.cols.push(new GridCol({ content: 'Produto' }))
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.datetime_friendly, size: 3 }))
        grid.cols.push(new GridCol({ content: 'Estoque', type: GridColTypeEnum.float_2, size: 2 }))
        grid.cols.push(new GridCol({ content: 'Situação', type: GridColTypeEnum.badge, size: 2 }))
        for (const item of items) {
            const vencido = new Date(item.nextExpirationDate) < new Date()
            const line = new GridLine()
            line.slots.push({ content: item.name })
            line.slots.push({ content: item.nextExpirationDate })
            line.slots.push({ content: item.stock })
            line.slots.push({
                content: new GridBadge({
                    label: vencido ? 'Vencido' : 'A vencer',
                    class: vencido ? GridBadgeClassEnum.badge_danger : GridBadgeClassEnum.badge_alert,
                }),
            })
            grid.lines.push(line)
        }
        expirationGrid = grid
    }

    function loadMinStockGrid(services: Service[]) {
        const items = services.filter((el) => el.minStock !== null && el.minStock !== undefined && el.stock <= el.minStock).sort((a, b) => a.stock - b.stock)

        const grid = baseGrid(
            'Produtos com estoque mínimo',
            'Produtos cujo estoque atual está igual ou abaixo do mínimo configurado no cadastro. "Crítico" quando o estoque está abaixo de 15% do mínimo.',
        )
        grid.cols.push(new GridCol({ content: 'Produto' }))
        grid.cols.push(new GridCol({ content: 'Estoque atual', type: GridColTypeEnum.float_2, size: 2 }))
        grid.cols.push(new GridCol({ content: 'Mínimo', type: GridColTypeEnum.float_2, size: 2 }))
        grid.cols.push(new GridCol({ content: 'Situação', type: GridColTypeEnum.badge, size: 2 }))
        for (const item of items) {
            const critico = item.stock < item.minStock * 0.15
            const line = new GridLine()
            line.slots.push({ content: item.name })
            line.slots.push({ content: item.stock })
            line.slots.push({ content: item.minStock })
            line.slots.push({
                content: new GridBadge({
                    label: critico ? 'Crítico' : 'Alerta',
                    class: critico ? GridBadgeClassEnum.badge_danger : GridBadgeClassEnum.badge_alert,
                }),
            })
            grid.lines.push(line)
        }
        minStockGrid = grid
    }

    function loadOpenUnitsGrid(openUnits: ProductOpenUnit[], loggedSums: { productOpenUnitId: string; logged: number }[]) {
        const items = [...openUnits].sort((a, b) => new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime())

        const grid = baseGrid(
            'Insumos abertos',
            'Insumos fracionados abertos no momento, ainda não finalizados. "Consumo declarado" é o valor informado no agendamento. Caso um mesmo item tenha mais de 1 unidade aberta, o consumo sempre será lançado no item com menor consumo registrado.',
        )
        grid.cols.push(new GridCol({ content: 'Insumo' }))
        grid.cols.push(new GridCol({ content: 'Aberto em', type: GridColTypeEnum.datetime_friendly, size: 3 }))
        grid.cols.push(new GridCol({ content: 'Volume nominal', size: 2 }))
        grid.cols.push(new GridCol({ content: 'Consumo', size: 2 }))
        if (canCorrectUnits) grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button, size: 3 }))
        for (const item of items) {
            const logged = loggedSums.find((el) => el.productOpenUnitId === item.productOpenUnitId)?.logged || 0
            const name = serviceMap[item.serviceId]?.name || '-'
            const line = new GridLine()
            line.slots.push({ content: name })
            line.slots.push({ content: item.openedAt })
            line.slots.push({ content: item.nominalVolume + ' ' + (item.volumeUnit || '') })
            line.slots.push({ content: logged + ' ' + (item.volumeUnit || '') })
            if (canCorrectUnits) {
                const buttons = [new GridButton({ label: 'Fechar frasco', callback: () => closeUnit(item, name, logged) })]
                // sem consumo declarado a abertura pode ter sido indevida (ex: frasco aberto em duplicidade)
                if (!(logged > 0)) buttons.push(new GridButton({ label: 'Desfazer abertura', callback: () => voidOpening(item, name) }))
                line.slots.push({ content: buttons })
            }
            grid.lines.push(line)
        }
        openUnitsGrid = grid
    }

    function loadVarianceGrid(closedUnits: ProductOpenUnit[]) {
        const items = closedUnits
            .filter((el) => el.variance !== null && el.variance !== undefined && el.variance !== 0)
            .sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime())
            .slice(0, 10)

        const grid = baseGrid('Insumos finalizados', 'Insumos que foram marcados como finalizados na agenda ou no estoque. Listando últimos 10 insumos finalizados.')
        grid.cols.push(new GridCol({ content: 'Insumo' }))
        grid.cols.push(new GridCol({ content: 'Fechado em', type: GridColTypeEnum.datetime_friendly, size: 3 }))
        grid.cols.push(new GridCol({ content: 'Volume nominal', size: 2 }))
        grid.cols.push(new GridCol({ content: 'Consumo', size: 2 }))
        if (canCorrectUnits) grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button, size: 3 }))
        for (const item of items) {
            const declared = item.nominalVolume - item.variance
            const name = serviceMap[item.serviceId]?.name || '-'
            const line = new GridLine()
            line.slots.push({ content: name })
            line.slots.push({ content: item.closedAt })
            line.slots.push({ content: item.nominalVolume + ' ' + (item.volumeUnit || '') })
            line.slots.push({ content: declared + ' ' + (item.volumeUnit || '') })
            if (canCorrectUnits) {
                const buttons = [new GridButton({ label: 'Reabrir frasco', callback: () => reopenUnit(item, name) })]
                if (!(declared > 0)) buttons.push(new GridButton({ label: 'Desfazer abertura', callback: () => voidOpening(item, name) }))
                line.slots.push({ content: buttons })
            }
            grid.lines.push(line)
        }
        varianceGrid = grid
    }

    function volumeLabel(unit: ProductOpenUnit, value: number) {
        return (value + ' ' + (unit.volumeUnit || '')).trim()
    }

    async function runUnitAction(action: () => Promise<unknown>) {
        try {
            show = true
            await action()
            await loadDashboard()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function closeUnit(unit: ProductOpenUnit, name: string, logged: number) {
        const difference = unit.nominalVolume - logged
        let message = `Fechar o frasco de ${name}?\n\nConsumo declarado: ${volumeLabel(unit, logged)} de ${volumeLabel(unit, unit.nominalVolume)}.`
        if (!(logged > 0)) {
            message = `Fechar o frasco de ${name}?\n\nNenhum consumo foi declarado neste frasco. Se ele foi aberto por engano, use "Desfazer abertura" para devolvê-lo ao estoque.`
        }
        if (difference > 0) message += `\nA diferença de ${volumeLabel(unit, difference)} ficará registrada em "Insumos finalizados".`
        if (!confirm(message)) return
        await runUnitAction(() => window.api.pos.stock.productConsumption.closeUnit(unit.productOpenUnitId))
    }

    async function reopenUnit(unit: ProductOpenUnit, name: string) {
        const message = `Reabrir o frasco de ${name}?\n\nEle volta para "Insumos abertos" e pode voltar a receber consumos lançados na agenda. A data e o responsável pela finalização serão descartados.`
        if (!confirm(message)) return
        await runUnitAction(() => window.api.pos.stock.productConsumption.reopenUnit(unit.productOpenUnitId))
    }

    async function voidOpening(unit: ProductOpenUnit, name: string) {
        const message = `Desfazer a abertura do frasco de ${name}?\n\n1 unidade volta para o estoque e o frasco deixa de aparecer como aberto ou finalizado. Use apenas se o frasco não foi aberto de verdade.`
        if (!confirm(message)) return
        await runUnitAction(() => window.api.pos.stock.productConsumption.voidOpening(unit.productOpenUnitId))
    }

    function loadRecentConsumptionGrid(logs: ProductConsumptionLog[], discreteConsumptions: ProductMovement[]) {
        // une consumo fracionado (log) e discreto (movimento out_consumption) numa única listagem —
        // são registrados em tabelas diferentes, mas pro dono ambos são só "consumo recente".
        const fromLogs = logs.map((el) => ({
            name: serviceMap[el.serviceId]?.name || '-',
            movedAt: el.movedAt,
            quantity: el.quantity,
            unit: serviceMap[el.serviceId]?.volumeUnit || '',
            appointmentId: el.appointmentId,
        }))
        const fromMovements = discreteConsumptions.map((el) => ({
            name: el.service?.name || serviceMap[el.serviceId]?.name || '-',
            movedAt: el.movedAt,
            quantity: el.quantity,
            unit: 'un',
            appointmentId: el.appointmentId,
        }))
        const items = [...fromLogs, ...fromMovements].sort((a, b) => new Date(b.movedAt).getTime() - new Date(a.movedAt).getTime()).slice(0, 15)

        const grid = baseGrid('Consumo recente', 'Últimos lançamentos de consumo de insumos registrados pela agenda nos últimos 7 dias.')
        grid.cols.push(new GridCol({ content: 'Insumo' }))
        grid.cols.push(new GridCol({ content: 'Quando', type: GridColTypeEnum.datetime_friendly, size: 3 }))
        grid.cols.push(new GridCol({ content: 'Quantidade', size: 2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button, size: 2 }))
        for (const item of items) {
            const line = new GridLine()
            line.slots.push({ content: item.name })
            line.slots.push({ content: item.movedAt })
            line.slots.push({ content: item.quantity + ' ' + item.unit })
            line.slots.push({
                content: item.appointmentId ? [new GridButton({ label: 'Ver', callback: () => (viewAppointmentId = item.appointmentId) })] : [],
            })
            grid.lines.push(line)
        }
        recentConsumptionGrid = grid
    }

    function loadPendingSyncGrid(failedSync: AppointmentConsumption[]) {
        const grid = baseGrid(
            'Pendências de sincronização',
            'Consumos lançados na agenda que falharam ao sincronizar com o estoque e precisam de conferência manual.',
        )
        grid.cols.push(new GridCol({ content: 'Insumo' }))
        grid.cols.push(new GridCol({ content: 'Quantidade', type: GridColTypeEnum.float_2, size: 2 }))
        grid.cols.push(new GridCol({ content: 'Erro', size: 5 }))
        for (const item of failedSync) {
            const line = new GridLine()
            line.slots.push({ content: serviceMap[item.serviceId]?.name || '-' })
            line.slots.push({ content: item.quantity })
            line.slots.push({ content: item.syncError || '-' })
            grid.lines.push(line)
        }
        pendingSyncGrid = grid
    }
</script>

{#if viewAppointmentId}
    <Popover title="Agendamento" width="1080px" height="80vh" on:close={() => (viewAppointmentId = null)}>
        <AppointmentForm appointmentId={viewAppointmentId} on:change={() => (viewAppointmentId = null)} on:cancel={() => (viewAppointmentId = null)} />
    </Popover>
{/if}

<Content footer={true}>
    <ZContainer>
        <ZContainerCol size={6} sizeM={12}>
            <Zgrid grid={expirationGrid} />
        </ZContainerCol>
        <ZContainerCol size={6} sizeM={12}>
            <Zgrid grid={minStockGrid} />
        </ZContainerCol>
        <ZContainerCol size={6} sizeM={12}>
            <Zgrid grid={openUnitsGrid} />
        </ZContainerCol>
        <ZContainerCol size={6} sizeM={12}>
            <Zgrid grid={varianceGrid} />
        </ZContainerCol>
        <ZContainerCol size={6} sizeM={12}>
            <Zgrid grid={recentConsumptionGrid} />
        </ZContainerCol>
        <ZContainerCol size={6} sizeM={12}>
            <Zgrid grid={pendingSyncGrid} />
        </ZContainerCol>
    </ZContainer>

    <LoadSpinner {show} />
</Content>
